import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/services/api';
import { hasCapability } from '@/lib/capabilities';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ChevronLeft, CreditCard, Banknote, Smartphone, QrCode, Loader2 } from 'lucide-react';

export default function PaymentSettings() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const storeId = user?.store_id;
  const canConfig = hasCapability(user, 'payments.config');

  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user?.id) { navigate('/login'); return; }
    if (!canConfig) { toast.error('No permission'); navigate('/pos-dashboard'); return; }
    apiClient.get(`/payments/settings/${storeId}`, { params: { _: Date.now() } }).then((r) => setSettings(r.data)).catch(() => toast.error('Failed to load'));
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await apiClient.put(`/payments/settings/${storeId}`, settings);
      setSettings(r.data);
      toast.success('Payment settings saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const setMethod = (k, v) => setSettings((s) => ({ ...s, methods: { ...s.methods, [k]: v } }));

  if (!settings) return <div className="p-8 text-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="payment-settings-page">
      <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3 sticky top-0 z-30">
        <Button variant="ghost" size="sm" onClick={() => navigate('/pos-dashboard')} data-testid="ps-back"><ChevronLeft className="w-4 h-4" /></Button>
        <div className="flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-sspice" />
          <div>
            <h1 className="font-heading text-lg font-bold text-slate-900">Payment Settings</h1>
            <p className="text-xs text-slate-500">Configure how this store collects payments</p>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto p-4 space-y-5">
        {/* UPI mode */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm font-semibold text-slate-800 mb-1">UPI Collection Mode</p>
          <p className="text-xs text-slate-500 mb-3">How UPI payments are confirmed at the counter.</p>
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="ps-upi-manual" onClick={() => setSettings({ ...settings, upi_mode: 'manual' })}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border ${settings.upi_mode === 'manual' ? 'border-sspice bg-red-50' : 'border-slate-200'}`}>
              <Smartphone className={`w-6 h-6 ${settings.upi_mode === 'manual' ? 'text-sspice' : 'text-slate-400'}`} />
              <span className="text-sm font-semibold">Manual Confirmation</span>
              <span className="text-[11px] text-slate-400 text-center">Cashier confirms UPI manually</span>
            </button>
            <button data-testid="ps-upi-qr" onClick={() => setSettings({ ...settings, upi_mode: 'razorpay_qr' })}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border ${settings.upi_mode === 'razorpay_qr' ? 'border-sspice bg-red-50' : 'border-slate-200'}`}>
              <QrCode className={`w-6 h-6 ${settings.upi_mode === 'razorpay_qr' ? 'text-sspice' : 'text-slate-400'}`} />
              <span className="text-sm font-semibold">Razorpay Dynamic QR</span>
              <span className="text-[11px] text-slate-400 text-center">Auto-confirmed via Razorpay</span>
            </button>
          </div>
        </div>

        {/* Enabled methods */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <p className="text-sm font-semibold text-slate-800">Accepted Payment Methods</p>
          {[
            { k: 'cash', label: 'Cash', icon: Banknote },
            { k: 'card', label: 'Card', icon: CreditCard },
            { k: 'upi', label: 'UPI', icon: Smartphone },
          ].map(({ k, label, icon: Icon }) => (
            <div key={k} className="flex items-center justify-between">
              <span className="flex items-center gap-2 text-sm text-slate-700"><Icon className="w-4 h-4 text-slate-400" /> {label}</span>
              <Switch checked={!!settings.methods?.[k]} onCheckedChange={(v) => setMethod(k, v)} data-testid={`ps-method-${k}`} />
            </div>
          ))}
        </div>

        {/* Delivery Payment Mode */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <p className="text-sm font-semibold text-slate-800 mb-1">Delivery Payment Mode</p>
          <p className="text-xs text-slate-500 mb-3">How delivery orders are paid. Deliveries are handled by external riders.</p>
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="ps-delivery-prepaid" onClick={() => setSettings({ ...settings, delivery_payment_mode: 'prepaid' })}
              className={`flex flex-col items-center gap-1 p-4 rounded-xl border ${(settings.delivery_payment_mode || 'prepaid') === 'prepaid' ? 'border-sspice bg-red-50' : 'border-slate-200'}`}>
              <span className="text-sm font-semibold">Prepaid Only</span>
              <span className="text-[11px] text-slate-400 text-center">Recommended — no cash on delivery</span>
            </button>
            <button data-testid="ps-delivery-cod" onClick={() => setSettings({ ...settings, delivery_payment_mode: 'cod' })}
              className={`flex flex-col items-center gap-1 p-4 rounded-xl border ${settings.delivery_payment_mode === 'cod' ? 'border-sspice bg-red-50' : 'border-slate-200'}`}>
              <span className="text-sm font-semibold">Cash on Delivery</span>
              <span className="text-[11px] text-slate-400 text-center">COD workflow not enabled</span>
            </button>
          </div>
        </div>

        <Button className="w-full bg-sspice hover:bg-sspice-hover" onClick={save} disabled={saving} data-testid="ps-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Settings'}
        </Button>
      </div>
    </div>
  );
}
